import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';

export interface ChildrenItem {
  id: string;
  description: string;
  format?: string;
}

export interface ModelItem {
  id: string;
  description: string;
  format?: string;
  children?: ChildrenItem[];
}

export interface GeneralQuestion {
  id: string;
  description: string;
}

export interface ClassificationItem {
  id: string;
  description: string;
  examples: string[];
}

export interface Model {
  id?: string | number;
  name: string;
  model_type?: string;
  user_list?: ModelItem[] | ClassificationItem[];
  general_rules?: string[];
  general_questions?: GeneralQuestion[];
  references?: string[];
  version?: string;
  created_at?: string;
  updated_at?: string;
  is_deployed?: boolean;
  operations?: any[];
  tag?: {
    id: string;
    name: string;
    text_color: string;
    bg_color: string;
  } | null;
  [key: string]: any;
}

export interface PaginatedModelsResponse {
  models: Model[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
  message?: string;
}

@Injectable({ providedIn: 'root' })
export class ModelsService {
  private baseUrl = '/api/models/';

  constructor(private http: HttpClient) {}

  list(tag?: string, search?: string, page: number = 1, pageSize: number = 10, isDeployed?: boolean): Observable<PaginatedModelsResponse> {
    let url = `${this.baseUrl}?page=${page}&page_size=${pageSize}`;
    if (tag) {
      url += `&tag=${encodeURIComponent(tag)}`;
    }
    if (search) {
      url += `&search=${encodeURIComponent(search)}`;
    }
    if (isDeployed !== undefined) {
      url += `&is_deployed=${isDeployed}`;
    }
    return this.http.get<PaginatedModelsResponse>(url);
  }

  getById(modelId: string): Observable<Model> {
    return this.http
      .get<{ message: string; model: Model }>(`${this.baseUrl}${modelId}`)
      .pipe(map(res => res.model));
  }

  create(model: Model): Observable<string> {
    return this.http.post<{ message: string; model_id: string }>(this.baseUrl, model).pipe(
      map(res => res.model_id)
    );
  }

  update(modelId: string, model: Model): Observable<string> {
    return this.http.put(`${this.baseUrl}${modelId}`, model, {
      responseType: 'text',
    });
  }

  delete(modelId: string, force: boolean = false): Observable<string> {
    return this.http.delete(`${this.baseUrl}${modelId}?force=${force}`, {
      responseType: 'text',
    });
  }
}